import { useEffect, useState } from "react";
import { X, Sparkles, ArrowRight } from "lucide-react";

/** One-shot promotional popup for the $79.99 starter dose.
 *  Auto-shows ~4s after landing (once per browser session) and can be
 *  dismissed by the big top-right X, backdrop click, or ESC. */
const STORAGE_KEY = "glpflo-starter-popup-dismissed";
const DELAY_MS = 4000;

export default function StarterDosePopup({ onQualify }) {
  const [open, setOpen] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    let dismissed = false;
    try {
      dismissed = sessionStorage.getItem(STORAGE_KEY) === "1";
    } catch { /* SSR / privacy modes */ }
    if (dismissed) return;
    const t = setTimeout(() => {
      setOpen(true);
      requestAnimationFrame(() => setVisible(true));
    }, DELAY_MS);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") handleClose(); };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const handleClose = () => {
    setVisible(false);
    try { sessionStorage.setItem(STORAGE_KEY, "1"); } catch { /* ignore */ }
    setTimeout(() => setOpen(false), 200);
  };

  const handleQualify = () => {
    handleClose();
    if (typeof onQualify === "function") {
      setTimeout(() => onQualify(), 220);
    }
  };

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="starter-dose-popup-title"
      className={`fixed inset-0 z-[1000] flex items-center justify-center px-4 sm:px-6 transition-opacity duration-200 ${visible ? "opacity-100" : "opacity-0"}`}
      data-testid="starter-dose-popup"
      onClick={handleClose}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-slate-900/70 backdrop-blur-sm" aria-hidden="true" />

      {/* Card */}
      <div
        onClick={(e) => e.stopPropagation()}
        className={`relative w-full max-w-md sm:max-w-lg rounded-3xl overflow-hidden shadow-2xl border border-purple-200/50 transform transition-all duration-200 ${visible ? "scale-100 translate-y-0" : "scale-95 translate-y-2"}`}
        style={{
          background: "linear-gradient(140deg, #4c1d95 0%, #6b21a8 45%, #a21caf 100%)",
        }}
      >
        {/* Close button — big, obvious, top-right */}
        <button
          type="button"
          onClick={handleClose}
          aria-label="Close promotional popup"
          className="absolute top-3 right-3 z-10 w-10 h-10 flex items-center justify-center rounded-full bg-white/95 hover:bg-white text-slate-800 hover:text-slate-950 shadow-lg hover:shadow-xl transition-all hover:scale-110 focus:outline-none focus:ring-4 focus:ring-white/60"
          data-testid="starter-dose-popup-close"
        >
          <X className="w-6 h-6" strokeWidth={2.5} />
        </button>

        {/* Sparkles decoration */}
        <Sparkles aria-hidden="true" className="absolute top-6 left-6 w-5 h-5 text-fuchsia-200/70 animate-pulse" />
        <Sparkles aria-hidden="true" className="absolute bottom-6 right-10 w-4 h-4 text-purple-200/70 animate-pulse" style={{ animationDelay: "0.8s" }} />

        <div className="px-6 sm:px-9 pt-12 pb-8 sm:pt-14 sm:pb-10 text-center">
          <p className="text-[10px] uppercase tracking-[0.35em] text-fuchsia-200 font-semibold mb-3">
            Limited Introductory Offer
          </p>
          <h2
            id="starter-dose-popup-title"
            className="text-3xl sm:text-4xl font-bold text-white mb-2 leading-tight"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            Start GLP-1 today for
          </h2>
          <div className="flex items-baseline justify-center gap-2 mb-4">
            <span className="text-5xl sm:text-6xl font-extrabold text-white drop-shadow" style={{ fontFamily: "'Outfit', sans-serif" }}>
              $79
              <span className="text-3xl sm:text-4xl align-top">.99</span>
            </span>
          </div>
          <p className="text-sm sm:text-base text-purple-100/90 mb-6 leading-relaxed max-w-sm mx-auto">
            Your first-month starter dose — physician-reviewed, pharmacy-compounded, shipped in a temperature-controlled box.
          </p>

          {/* CTA */}
          <button
            type="button"
            onClick={handleQualify}
            className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-white text-purple-800 font-bold text-base sm:text-lg shadow-xl hover:shadow-2xl hover:bg-purple-50 transition-all hover:scale-[1.02] focus:outline-none focus:ring-4 focus:ring-fuchsia-300/60 w-full sm:w-auto"
            data-testid="starter-dose-popup-qualify-btn"
          >
            <Sparkles className="w-5 h-5" />
            See if I qualify
            <ArrowRight className="w-5 h-5" />
          </button>

          <button
            type="button"
            onClick={handleClose}
            className="mt-4 text-xs text-purple-200/80 hover:text-white transition-colors underline underline-offset-2"
            data-testid="starter-dose-popup-dismiss-link"
          >
            No thanks, keep browsing
          </button>

          <p className="mt-5 text-[10px] uppercase tracking-widest text-fuchsia-200/70">
            Provider consult required • Not all patients qualify
          </p>
        </div>
      </div>
    </div>
  );
}
